import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { ArrowLeft, Shield, AlertTriangle, FileCheck, Scale, Eye, Ban, BookOpen, Gavel } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TermsPage = () => {
  const [terms, setTerms] = useState(null);

  const fetchTerms = useCallback(() => {
    axios.get(`${API_URL}/api/terms`).then(r => setTerms(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchTerms();
  }, [fetchTerms]);

  const sections = [
    { icon: <Shield className="h-5 w-5" />, title: "Platform Role", key: "vetting_role" },
    { icon: <Ban className="h-5 w-5" />, title: "Non-Agency Clause", key: "non_agency", emphasis: true },
    { icon: <BookOpen className="h-5 w-5" />, title: "No Advice Clause", key: "no_advice", emphasis: true },
    { icon: <AlertTriangle className="h-5 w-5" />, title: "No Guarantees", key: "no_guarantee" },
    { icon: <FileCheck className="h-5 w-5" />, title: "Independent Verification Required", key: "independent_verification" },
    { icon: <Gavel className="h-5 w-5" />, title: "Due Diligence Warning", key: "due_diligence", critical: true },
    { icon: <Scale className="h-5 w-5" />, title: "Professional Advice", key: "professional_advice" },
    { icon: <Eye className="h-5 w-5" />, title: "AI Valuation Disclaimer", key: "ai_valuation_disclaimer" },
    { icon: <Shield className="h-5 w-5" />, title: "Payment & Refund Policy", key: "payment_terms" },
    { icon: <AlertTriangle className="h-5 w-5" />, title: "Liability Waiver", key: "liability_waiver", emphasis: true },
    { icon: <AlertTriangle className="h-5 w-5" />, title: "General Limitation of Liability", key: "liability" },
    { icon: <Eye className="h-5 w-5" />, title: "Data & Privacy (GDPR)", key: "data_use" },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-3xl mx-auto px-4 py-16">
        <Link to="/" className="inline-flex items-center text-slate-400 hover:text-[#1A1A1A] mb-8 text-sm tracking-wider" data-testid="back-link">
          <ArrowLeft className="h-4 w-4 mr-2" />BACK
        </Link>

        <div className="mb-10">
          <h1 className="font-['Playfair_Display'] text-4xl text-[#1A1A1A] mb-3" data-testid="terms-heading">Terms & Conditions</h1>
          <p className="text-slate-500 text-base">Please read these terms carefully before using Shopperden</p>
          {terms && <p className="text-xs text-slate-400 mt-2">Version {terms.version} | Last updated: {terms.last_updated}</p>}
        </div>

        {/* Mandatory Safe Harbor Clause — IT (Intermediary Guidelines) Rules, India */}
        <div className="bg-[#0A0A0C] text-white border-2 border-[#D4AF37] p-6 mb-6" data-testid="terms-safe-harbor">
          <div className="flex items-start gap-3">
            <Shield className="h-6 w-6 text-[#D4AF37] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-[10px] tracking-[0.3em] text-[#D4AF37] uppercase font-bold mb-2">Mandatory Safe Harbor Notice (IT Intermediary Guidelines)</p>
              <p className="text-white text-sm leading-relaxed">
                This platform is an <b>automated intermediary</b>. It does not verify the title of
                properties, the identity of users, or the legality of listings. The fee charged is
                strictly for <b>AI-driven data processing and digital advertising visibility</b>, not
                for real estate brokerage or consultancy.
              </p>
              <p className="text-white/60 text-xs leading-relaxed mt-3">
                Shopperden Ltd operates under the Information Technology (Intermediary Guidelines and
                Digital Media Ethics Code) Rules, 2021 (India) and equivalent intermediary safe-harbor
                provisions in the United Kingdom and Canada. Users are responsible for independently
                verifying any property, identity, title, or transaction before acting on information
                surfaced through Shopperden's AI matchmaking and discovery services.
              </p>
            </div>
          </div>
        </div>

        {/* Summary Banner */}
        <div className="bg-[#FEF3EC] border border-[#E07A3A]/20 p-6 mb-8" data-testid="terms-summary">
          <div className="flex items-start gap-3">
            <Shield className="h-6 w-6 text-[#E07A3A] mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-[#1A1A1A] mb-1">Key Summary</h3>
              <p className="text-[#1A1A1A]/70 text-sm leading-relaxed">
                {terms?.summary || "Shopperden is a property introduction service only. Final decisions must be based on independent professional advice. Service fees are non-refundable."}
              </p>
            </div>
          </div>
        </div>

        {/* Terms Sections */}
        <div className="space-y-6">
          {sections.map((section, idx) => (
            <div
              key={section.key}
              className={`luxury-card p-6 ${section.critical ? 'border-2 border-red-400 bg-red-50/30' : section.emphasis ? 'border-l-4 border-l-[#E07A3A]' : ''}`}
              data-testid={`terms-section-${section.key}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 ${section.critical ? 'bg-red-100 text-red-600' : section.emphasis ? 'bg-[#FEF3EC] text-[#E07A3A]' : 'bg-[#F8FAFC] text-[#E07A3A]'}`}>
                  {section.icon}
                </div>
                <div>
                  <h3 className={`font-['Playfair_Display'] text-lg font-semibold mb-2 ${section.critical ? 'text-red-700' : 'text-[#1A1A1A]'}`}>
                    {idx + 1}. {section.title}
                  </h3>

                  {section.critical ? (
                    <div className="space-y-3">
                      <p className="text-red-800 text-sm leading-relaxed font-bold">
                        {terms?.disclaimer?.[section.key] || "Loading..."}
                      </p>
                      <div className="bg-red-100 border border-red-300 p-4 mt-3">
                        <p className="text-red-800 text-sm font-bold flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                          IMPORTANT: Always use qualified Solicitors and RICS-certified Surveyors before exchanging money or signing contracts.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className={`text-sm leading-relaxed ${section.emphasis ? 'text-[#1A1A1A]/70 font-medium' : 'text-[#1A1A1A]/60'}`}>
                      {terms?.disclaimer?.[section.key] || "Loading..."}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact */}
        <div className="mt-10 text-center text-sm text-slate-400">
          <p>For queries regarding these terms, contact our support team.</p>
          <p className="mt-1">Shopperden - Your Trusted Property Platform</p>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
